import { defineStore } from "pinia";
import { MarketplaceApi } from "~/api/marketplace";

import useConvert from "~/composables/useConvert";
import {
  Scarcity,
  AuctionUpdate,
  MarketType,
  Auction,
  Pagination,
  NftSkill,
  NftEquipment,
} from "fungi-types";
import { BigNumber } from "ethers";
import { auctionApi } from "~/api/auction";
import { MarketPlaceFilter } from "~/api/auction/types";

const { maticToWei, usdcToUsd, usdToUsdc } = useConvert;

export const usePrimaryMarketplace = defineStore("primaryMarketplace", {
  state: () => ({
    marketType: MarketType.PRIMARY,
    api: new MarketplaceApi(MarketType.PRIMARY),
    auctions: [] as (NftSkill | NftEquipment)[],
    stickyAuctions: [] as NftSkill[],
    pagination: {
      page: 1,
      limit: 20,
    } as Pagination,
    filters: {} as Partial<MarketPlaceFilter>,
    loading: false,
  }),

  getters: {
    activeFilters(state) {
      return Object.values(state.filters).reduce((count: number, value) => {
        if (
          ((typeof value === "string" || Array.isArray(value)) && value.length > 0) ||
          (typeof value === "boolean" && value === true)
        ) {
          return count + 1;
        }
        return count;
      }, 0);
    },
  },

  actions: {
    filterStickyAuctions(nfts: Array<NftSkill>) {
      return nfts.filter(
        (nft) =>
          this.stickyAuctions.findIndex((sticky) => sticky.auction?.cuid === nft.auction?.cuid) ===
          -1
      );
    },
    async getAuctionByCuid(cuid: string) {
      const response = await this.api.getCards({ auctionId: cuid });
      return response.data.length > 0 ? response.data[0] : null;
    },
    async getCards() {
      try {
        const resp = await auctionApi.auctions.primary.list.query({
          page: 1,
          ...this.filters,
        });
        this.auctions = resp.result;
        this.pagination = resp.pagination;
        this.loading = false;
      } catch (e) {
        console.log("e", e);
      }
    },

    async updateFilters(filters: Partial<MarketPlaceFilter>) {
      this.filters = { ...filters };
      if (filters.price?.[0] || filters.price?.[1]) {
        filters.price[0] = usdToUsdc(+filters.price[0]).toString();
        filters.price[1] = usdToUsdc(+filters.price[1]).toString();
      }
      await this.getCards();
    },

    async nextPage() {
      this.loading = true;
      if (this.pagination.hasNextPage) {
        const response = await auctionApi.auctions.primary.list.query({
          page: this.pagination.page + 1,
          limit: this.pagination.limit,
          ...this.filters,
        });
        this.auctions.push(...response.result);
        this.pagination = response.pagination;
      }
      this.loading = false;
    },

    async resetFilters() {
      this.filters = {} as MarketPlaceFilter;
      await this.getCards();
    },

    async updateAuction(
      data: AuctionUpdate,
      auctionsSrc?: (NftSkill | NftEquipment)[]
    ): Promise<{
      prevBidder: { username: string | undefined; cuid: string | undefined } | null;
      item: NftSkill | NftEquipment;
      index: number;
    } | null> {
      const nfts = auctionsSrc ?? this.auctions;

      // Find the auction to update
      const index = nfts.findIndex((nft) => nft.auction?.cuid === data.auction.cuid);

      if (index === -1) return null;
      const nft = nfts[index];
      if (!nft.auction) {
        console.warn("No auction found for the given cuid:", data.auction.cuid);
        return null;
      }

      // Validate that new bid is after and has a higher amount than the last known bid
      const newBidAmount = BigNumber.from(data.auction.lastBidAmount);
      const lastBidAmount = BigNumber.from(nft.auction?.lastBidAmount ?? "0");
      const newBidAt = new Date(data.auction.lastBidAt);
      const lastBidAt = new Date(nft.auction?.lastBidAt ?? "");
      const isNewBidBeforeLastKnownBid = newBidAt.getTime() < lastBidAt.getTime();
      const isNewBidLesserThanLastKnownBid = newBidAmount.lt(lastBidAmount);
      if (isNewBidBeforeLastKnownBid || isNewBidLesserThanLastKnownBid) return null;

      // Get the last known bidder info before overwriting the auction's data
      const prevBidder = {
        username: nft.auction?.lastBidder?.username,
        cuid: nft.auction?.lastBidder?.cuid,
      };

      const updatedAuction: Auction = {
        ...nft.auction,
        lastBidder: {
          username: data.bidder.username,
          cuid: data.bidder.cuid,
        },
        lastBidAt: data.auction.lastBidAt as string,
        endedAt: data.auction.endedAt as string,
        lastBidAmount: data.auction.lastBidAmount,
        countOrders: data.auction.countOrders,
        status: data.auction.status,
      };

      nfts[index] = { ...nft, auction: { ...updatedAuction } };

      return { prevBidder, item: nfts[index], index };
    },
  },
});
